import React from 'react';
import { useTranslation } from 'react-i18next';

import DefaultLayout from '../../components/Layout/DefaultLayout';
import {
  AboutCampaignAndCharitySection,
  DonateButton,
  EventDetails,
  FacebookShare,
  FeatureImage,
  PageOwnerTitle,
  PageSection,
  Story,
  TotaliserWrapper,
  Totals,
} from '../../components';

import config from '../../client/config';
import CampaignsHelmet from '../../components/Helmet/CampaignsHelmet';
import CharityRegNumber from '../../components/CharityRegNumber/CharityRegNumber';
import FundraisersList from '../../components/FundraisersList/FundraisersList';
import SocialButtonsListWrapper from '../../components/SocialButtons/SocialButtonsListWrapper';
import SupportersList from '../../components/SupportersList/SupportersList';
import TeamsList from '../../components/TeamsList/TeamsList';
import DonationSummary from '../../components/DonationSummary/DonationSummary';
import Page from '../../components/Page/Page';
import useBasePageQuery from '../../hooks/useBasePageQuery';
import { useNotNullableContext } from '../../hooks/useNotNullableContext';
import { PageMetaContext } from '../../context/PageMetaContext';
import CampaignLogo from '../../components/CampaignLogo/CampaignLogo';
import StickySummary from '../../components/StickySummary/StickySummary';
import CampaignStartFundraising from '../../components/CampaignStartFundraising/CampaignStartFundraising';
import SuccessMessage from '../../components/SuccessMessage/SuccessMessage';
import WeDidItHeader from '../../components/WeDidItHeader/WeDidItHeader';
import Editbar from '../../components/Editbar/Editbar';
import IsQRCode from '../../components/IsQRCode/IsQRCode';
import QRModal from '../../components/QRModal/QRModal';
import ClosingDate from '../../components/ClosingDate/ClosingDate';

import '../Pages.scss';
import styles from './CampaignPage.scss';
import { Banner } from '../../components/Banner/Banner';
import PageAlert from '../../components/PageAlert/PageAlert';
import { useFlags } from '../../hooks/useFlags';
import { GoToImmersiveGiving } from '../../components/GoToImmersiveGiving/GoToImmersiveGiving';
import { useInView } from 'react-intersection-observer';

export const CampaignPage: React.FunctionComponent = () => {
  const [inViewRef, isInView] = useInView();
  const basePageData = useBasePageQuery();
  const { t } = useTranslation('common', { useSuspense: false });
  const { pageShortName } = useNotNullableContext(PageMetaContext);

  const { enableOtgImmersiveGiving } = useFlags();

  const data = basePageData?.data?.page;
  const legacyId = data && data.legacyId;
  const shareSubject = '';
  const shareMessage = '';
  const isOTGImmersiveGivingShortname =
    pageShortName === 'make-a-refugee-shelter-a-home';
  return (
    <Page>
      <IsQRCode>
        <QRModal ownerName={data?.title} />
      </IsQRCode>
      <CampaignsHelmet />
      <PageAlert />
      <Banner />
      <Editbar
        adminLink={`${config.baseUri}/campaign/manage/edit/${legacyId}`}
      />
      <SuccessMessage />
      <WeDidItHeader />
      <DefaultLayout
        slots={{
          featureImage: <FeatureImage enableLightbox />,
          ownerAvatar: <CampaignLogo />,
          titles: <PageOwnerTitle />,
          totals: (
            <>
              <TotaliserWrapper />
              <Totals />
            </>
          ),
          callsToAction: (
            <div ref={inViewRef} className={styles.ctaWrapper}>
              <DonateButton className="jg-space-mrsm jg-space-mrn@lg jg-space-mbsm@lg" />
              <FacebookShare showPrefix />
            </div>
          ),
          titlesSupplementary: (
            <>
              <ClosingDate />
              <EventDetails />
              <CharityRegNumber />
            </>
          ),
          causeMini:
            isOTGImmersiveGivingShortname &&
            enableOtgImmersiveGiving &&
            legacyId ? (
              <GoToImmersiveGiving />
            ) : (
              <CampaignStartFundraising />
            ),
          main: (
            <>
              <PageSection title={t('story.title')} hasBorder>
                <Story />
                <h3 className="jg-text--center">
                  {t('story.shareTitle', 'Share this story')}
                </h3>
                <SocialButtonsListWrapper
                  subject={shareSubject}
                  message={shareMessage}
                />
              </PageSection>
            </>
          ),
          aside: (
            <>
              <FundraisersList />
              <TeamsList />
              <SupportersList
                noSupportersMessage={t(
                  'campaign.noSupportersMessage',
                  'Try making a donation to get things going',
                )}
              />
            </>
          ),
          contentFooterPrimary: (
            <AboutCampaignAndCharitySection withExternalUrl={true} />
          ),
          contentFooterSecondary: (
            <DonationSummary
              fieldsToShow={[
                'directDonationAmount',
                'fundraiserRaisedAmount',
                'offlineAmount',
              ]}
            />
          ),
        }}
      />
      {isInView ? null : <StickySummary />}
    </Page>
  );
};

CampaignPage.displayName = 'CampaignPage';

export default CampaignPage;
